/**
 * Overview
 *
 *  Loading Process
 *  ===============
 *
 *  — Must exist before loading:
 *  	— `productLine`
 *  	— `breakpoints`
 *  — product line
 *  	— **action:** loaded in Fonts container *
 *  	— **selector:** `productLine: selectProductLine()`
 *  	— store `productLineID` in state?
 *  — product styles
 *  	— **action:** loaded in Fonts container
 *  	— **selector:** productStyles: selectProductStyles()
 *  — product features — *async*
 *  	— **action:** `onLoadProductFeatures()`
 *  	— **selector:** `productFeatures: selectProductFeatures()`
 *  		— update `this.state.loadingState.productFeatures`
 *  — layouts — *async*
 *  	— **action:** `onLoadTryLayout()`
 *  	— **selector:** `tryLayout: selectTryLayout()`
 *  		— decode layouts and store in state
 *  		2. set `activeLayoutType` based on current breakpoint
 *  		3. update `this.state.loadingState.try`
 *  — load woffs — *async*
 *  	— `this.loadWOFFs()`
 *  	— `this.injectLayout()`
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import autobind from 'class-autobind';
import jss from './jss';
import request from 'utils/request';
import * as Sentry from '@sentry/browser';
import classNames from 'classnames';

// Context
import { OverviewProvider } from './OverviewContext';
import ContextReducer from './ContextReducer';


// Config
import {
	defaultLabelFont as DefaultLabelFont,
	exceptions as Exceptions,
	layoutBreakpoints as LayoutBreakpoints,
	prefix as Prefix
} from './config';


// Utilities
import {
	decodeData,
	loadWOFFs
} from 'utils';
import {
	fontDetailsFromClassName,
	fontHasLigatures,
	setLayoutType
} from './utils';


// Lodash
import {
	chain,
	filter,
	find,
	forEach,
	get,
	includes,
	isEmpty,
	isEqual,
	keys,
	map,
	pickBy,
	slice
} from 'lodash';


// Initial state
import pureState from './pureState';


// Components
import Controls from 'components/Overview/Controls';
import Editor from 'components/Overview/Editor';
import ErrorBoundary from 'components/ErrorBoundary';
import Loading from 'components/Loading';
import SuggestedPairings from 'components/SuggestedPairings';

// CSS
import CSS from './styles.css';

// Actions
import {
	clearTryLayout,
	loadTryLayout,
	setEditorControlsOpen
} from 'store/fonts/actions';
import {
	setErrorModal,
	toggleFavorite
} from 'pages/App/actions';


// Selectors
import { createStructuredSelector } from 'reselect';
import {
	selectBreakpoints,
	selectError,
	selectFavoritedPackageIDs
} from 'pages/App/selectors';
import {
	selectProductLine,
	selectProducts,
	selectTryLayout,
	selectTryLayoutError,
	selectSuggestedPairings
} from 'store/fonts/selectors';


// Local config for utils
const CONFIG = {
	source: 'overview'
};


export class Overview extends React.PureComponent {
	isMounted = false;

	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			...pureState,
			previousBreakpoint: props.breakpoints[LayoutBreakpoints.desktop],
			favoritedPackageIDs: props.favoritedPackageIDs,		// array of already favorited package ids
			loadingError: false,

			// Actions for context reducer. Mimicking an action creators used in
			// Redux store. Used for updating individual settings, one at a time.
			putSetting: action => new Promise(resolve => {
				if (this.isMounted) {
					this.setState(state => ContextReducer(state, action), resolve);
				}
			}),

			putSettingSync: action =>
				this.setState(state => ContextReducer(state, action)),

			/**
			 * Load product features using productLine ID. Checks to see if product
			 * features for the active ID are already loaded into the productFeatures
			 * object.
			 *
			 * These JSON files are currently static. They’ll later be added to the DB
				* and an endpoint will be created.
				*
			 * @method getProductFeatures
			 * @param  {Number}           productLineID
			 * @return {Promise|Boolean}
			 */
			getProductFeatures: productLineID => new Promise(resolve => {
				if (!productLineID) { throw Error('Looks like you’ve forgotten to pass a productLineID.'); }

				const { putSetting } = this.state;
				const url = `/overview/productLineFeatures/pl_${productLineID}.json`;

				// If this product line’s features file is already loaded into the
				// `productFeatures` store, don’t load it again.
				const isFeaturesLoaded = get(this.state, `productFeatures[${productLineID}]`, false);
				if (isFeaturesLoaded) {
					resolve();
				} else {
					// Load up the font features for the product line and any other styles
					// that are included in the layout.
					request(url)
						.catch((error) => {
							this.setState({ loadingError: true });
							if (process.env.NODE_ENV === 'development' && process.env.LOG_ERRORS) {
								throw error;
							} else {
								Sentry.withScope(scope => {
									scope.setExtra('debug', false);
									Sentry.captureException(error);
								});
							}
						})
						.then(response => {
							putSetting({ type: 'SET_PRODUCT_FEATURES', productFeatures: response });
							resolve();
						});
				}
			})
		};

		this.Editor = React.createRef();
	}


	static getDerivedStateFromProps(props, state) {
		let updates = {};

		if (state.previousProductLineID !== props.productLine.id && props.productLine) {
			updates = {
				...updates,
				activeLayoutType: null,
				layoutIsInjected: false,
				loadingState: {
					...state.loadingState,
					productFeatures: false,
					try: false
				},
				previousProductLineID: get(props, 'productLine.id', null),
				setupStart: false
			};
		}

		if (!get(props, 'productLine.attributes.desktop_try_code', false)) {
			return {
				errorMessage: 'Missing Overview layout'
			};
		}

		/**
		 * Once the `tryLayout` is loaded, decode desktop and mobile layouts’
		 * CSS, JSS, and HTML, then save them to state.
		 */
		if (state.previousTryLayout !== props.tryLayout && props.tryLayout) {
			const desktopSource = JSON.parse(get(props, 'tryLayout.desktop'));
			const mobileSource = JSON.parse(get(props, 'tryLayout.mobile'));

			let layouts = {};

			if (desktopSource && mobileSource) {
				// These layouts should be left untouched. They’re set here as
				// an immutable source. When the state is being set, we copy the
				// html from the appropriate source (desktop or mobile) into
				// `editorContent`. When moving between mobile <-> desktop
				// breakpoints, the content is copied again from the
				// appropriate source.
				layouts = {
					...state.layouts,
					desktop: {
						...state.layouts.desktop,
						css: get(desktopSource, 'css'),
						html: decodeData(get(desktopSource, 'content')),
						jss: get(desktopSource, 'jss')
					},
					mobile: {
						...state.layouts.mobile,
						css: get(mobileSource, 'css'),
						html: decodeData(get(mobileSource, 'content')),
						jss: get(mobileSource, 'jss')
					}
				};
			}

			updates = {
				...updates,
				layouts,
				loadingState: {
					...state.loadingState,
					try: true
				},
				previousTryLayout: props.tryLayout
			};

			/**
			 * Set the `activeLayoutType` state. This alerts the app to which
			 * environment to respond to: mobile or desktop.
			 */
			const activeLayoutType = setLayoutType({ props, mobileSource: updates });

			updates = {
				...updates,
				activeLayoutType
			};

			return updates;
		}

		/**
		 * When the breakpoints change, update the `activeLayout` in state with
		 * the layout type we should load (desktop|mobile).
		 */
		if (
			state.previousBreakpoint !== props.breakpoints[LayoutBreakpoints.desktop]
		) {
			const activeLayoutType = setLayoutType({ props, mobileSource: state });
			updates = {
				...updates,
				activeLayoutType,
				previousActiveLayoutType: props.breakpoints[LayoutBreakpoints.desktop],
				previousBreakpoint: props.breakpoints[LayoutBreakpoints.desktop]
			};

			return updates;
		}

		/**
		 * Once the productFeatures request completes, update state.
		 */
		if (state.previousProductFeatures === null && state.productFeatures !== null) {
			updates = {
				...updates,
				loadingState: {
					...state.loadingState,
					productFeatures: true
				},
				previousProductFeatures: isEmpty(state.productFeatures) ? null : state.productFeatures
			};

			return updates;
		}


		return null;
	}


	async componentDidMount() {
		// Go!
		this.init();
	}


	async componentDidUpdate(prevProps, prevState) {
		if (
			this.props.products
			&& this.state.loadingState.productFeatures
			&& this.state.loadingState.try
			&& this.state.activeLayoutType
			&& !this.state.layoutIsInjected
			&& this.state.layouts.desktop.html
			&& this.state.layouts.desktop.jss
			&& !this.state.setupStart
		) {
			this.setup();
		}


		if (
			!prevState.interactionInitiated
			&& this.state.interactionInitiated
		) {
			this.props.onSetEditorControls(true);
		}


		if (
			prevState.activeLayoutType !== this.state.activeLayoutType
			&& prevState.activeLayoutType
			&& prevProps.productLine.id === this.props.productLine.id
		) {
			this.setup();
		}


		if (this.props.productLine.id !== prevProps.productLine.id) {
			this.runCleanup();
			await this.init();
		}

		// Save favorited package IDs to use in Suggested Pairings.
		if (
			(
				!isEqual(prevProps.favoritedPackageIDs, this.props.favoritedPackageIDs)
				|| isEmpty(prevProps.suggestedPairings) && !isEmpty(this.props.suggestedPairings)
			) &&
			get(this.props, 'favoritedPackageIDs')
		) {
			this.setState({
				favoritedPackageIDs: this.props.favoritedPackageIDs
			});
		}
	}


	componentWillUnmount() {
		this.isMounted = false;
		this.runCleanup();
	}


	/* eslint-disable react/sort-comp */
	async init() {
		this.isMounted = true;

		// Watch the router for the back button. You can’t use
		// `componentDidUpdate` because we’re changing routes. The
		// pathname will never be in a `!==` state.
		this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave);

		await this.loadLayout();
	}
	/* eslint-enable react/sort-comp */


	async setup() {
		const { putSetting } = this.state;

		putSetting({ type: 'SET_EDITOR_IS_READY', value: false });

		if (!get(this.props, 'productLine.attributes.desktop_try_code', false)) {
			throw Error('This product line has no layout.');
		}

		if (this.isMounted) {
			this.setState({
				fontsPartiallyLoaded: false,
				fontsFullyLoaded: false,
				loadControls: false,
				setupStart: true
			});
		}

		this.createStyleSheet();

		await this.parseDocumentStyles();

		await this.setFonts();

		const defaultStyleID = get(this.state, `productFeatures[${this.props.productLine.id}].value`);
		await this.setSelectedOpenTypeFeatures(defaultStyleID);

		// Inject parsed HTML from Try layout into the editor
		await this.injectLayout();
	}


	/**
	 * Get the active layout type based on the currently active breakpoint.
	 *
	 * @method getActiveLayout
	 * @return {String}        desktop|mobile
	 */
	getActiveLayout() {
		return setLayoutType({ props: this.props });
	}


	async setFamily(id) {
		const { putSetting } = this.state;
		await putSetting({ type: 'SET_FAMILY', id, source: CONFIG.source, silent: true });
	}


	/**
	 * Push the `products` data into the `fonts` item in the store.
	 *
	 * @method setFonts
	 * @return {Promise}
	 */
	async setFonts() {
		const { products } = this.props;
		const { putSetting } = this.state;
		await putSetting({ type: 'SET_FONTS', products, source: CONFIG.source });
	}


	async setStyle(id) {
		const { productLine } = this.props;
		const { putSetting } = this.state;

		await putSetting({ type: 'SET_STYLE', styleID: id, familyID: productLine.id, source: CONFIG.source });
	}


	/**
	 * Update OpenType features when a new product line is loaded.
	 *
	 * @method setSelectedOpenTypeFeatures
	 * @param  {Number}             styleID Selected style id
	 * @return {Void}             Sets state with the updates opentype features
	 */
	async setSelectedOpenTypeFeatures() {
		const { putSetting, settings } = this.state;

		// Update OpenType features when changing fonts.
		// This may be causing problems with element creation.
		const defaultFigureForSelectedStyle = settings.selectedStyle.defaultFigure;
		const hasLigatures = fontHasLigatures(settings.selectedStyle.features);
		let features;
		if (hasLigatures) {
			features = defaultFigureForSelectedStyle ? `liga-${defaultFigureForSelectedStyle}` : 'liga';
		} else { features = defaultFigureForSelectedStyle || ''; }

		await putSetting({ type: 'SET_OPENTYPE_FEATURES', value: features, silent: true, source: CONFIG.source });
	}


	createStyleSheet() {
		// Create the stylesheet that we’ll use for @font-face rules.
		this.styleSheetNode = document.createElement('style');
		document.head.appendChild(this.styleSheetNode);
		this.styleSheet = this.styleSheetNode.sheet;

		// Create the JSS stylesheet
		this.sheet = {
			desktop: jss.createStyleSheet().attach(),
			mobile: jss.createStyleSheet().attach()
		};
	}

	removeStylesheets() {
		if (get(this, 'styleSheetNode.parentNode', false)) {
			const parent = this.styleSheetNode.parentNode;
			parent.removeChild(this.styleSheetNode);
		}

		this.styleSheetNode = null;
		this.styleSheet = null;

		if (get(this.sheet)) {
			jss.removeStyleSheet(this.sheet.desktop);
			jss.removeStyleSheet(this.sheet.mobile);
		}
	}

	/**
	 * Watch history for the backbutton. When detected, clear the layout and reload.
	 *
	 * @method routerWillLeave
	 * @param  {String}        nextState
	 * @return {Promise}
	 */
	routerWillLeave(nextState) {
		if (nextState.action === 'POP') {
			this.runCleanup();
		}
	}


	/***
	 *      __   _     ____    __   _      _     ___
	 *    / /`  | |   | |_   / /\  | |\ | | | | | |_)
	 *    \_\_, |_|__ |_|__ /_/--\ |_| \| \_\_/ |_|
	 *
	 * — Make sure to add logic for detaching stylesheets for cleanup.
	 *
	 */
	runCleanup() {
		this.props.onClearTryLayout();

		// Remove the stylesheet that was created to house the @font-face declarations.
		this.removeStylesheets();

		this.props.onSetEditorControls(false);

		this.setState({
			...pureState,
			previousTryLayout: false,
			productFeatures: {},
			setupIsRunning: false,
			setupIsComplete: false,
			setupStart: false
		});
	}


	/**
	 * Run the initial methods to get the requested layout loaded.
	 *
	 * @method loadLayout
	 * @return {Void}
	 */
	async loadLayout() {
		// Load the mobile and desktop Try layouts
		await this.props.onLoadTryLayout();
	}

	getElementTreeClassNames(element) {
		let treeClassNames = element.className.split(' ');
		if (element.children.length > 0) {
			forEach(element.children, (child) => {
				treeClassNames = treeClassNames.concat(element.className.split(' '));
				treeClassNames = treeClassNames.concat(this.getElementTreeClassNames(child));
			});
		}
		return treeClassNames;
	}


	/**
* Responsible for collecting the JSS and HTML for the current breakpoint.
*
* @method injectLayout
* @return {Void}
*/
	async injectLayout() {
		const { activeLayoutType, layouts } = this.state;

		if (this.isMounted) {
			// Grab the JSS object for the active layout
			this.setState({ layoutIsInjected: false });
			const layoutJSS = get(layouts, `[${activeLayoutType}].jss`);
			const layoutHTML = get(layouts, `[${activeLayoutType}].html`);

			const layout = document.createElement('div');
			layout.innerHTML = layoutHTML;

			const descendents = layout.getElementsByTagName('P');
			const segmentLength = descendents.length < 10 ? 1 : Math.min(10, parseInt(descendents.length * 0.1, 10)); // eslint-disable-line no-magic-numbers
			const cssRules = { ...layoutJSS };
			if (this.sheet) {
				this.sheet[activeLayoutType].addRules(cssRules);
			}
			this.addWOFFRules(keys(cssRules));
			this.Editor.current.innerHTML = '';

			await this.loadDefaultLabelFont();
			await this.injectLayoutDescendentsSegment(descendents, segmentLength);
		}
	}

	async injectLayoutDescendentsSegment(descendents, segmentLength) {
		const { putSetting } = this.state;
		const descendentLength = descendents.length;

		const segment = slice(descendents, 0, segmentLength);
		const segmentCssClasses = chain(segment)
			.map((element) => this.getElementTreeClassNames(element))
			.flatten()
			.uniq()
			.value();

		await this.loadWOFFs(segmentCssClasses)
			.then(() => {
				putSetting({ type: 'INITIAL_SETUP_IS_COMPLETE', status: true });
				if (this.isMounted && !this.state.loadingError) {
					this.setState({
						fontsPartiallyLoaded: true,
						fontsFullyLoaded: descendentLength <= segmentLength,
						loadControls: true,
						setupStart: true
					});
				}
			});

		if (!this.state.loadingError) {
			forEach(segment, (element) => get(this, 'Editor.current.appendChild') && this.Editor.current.appendChild(element));

			putSetting({ type: 'UPDATE_LAYOUT', value: true });

			this.setState({ layoutIsInjected: true });

			if (descendentLength > segmentLength && this.isMounted) {
				await this.injectLayoutDescendentsSegment(descendents, segmentLength);
			}
		}
	}


	/**
	 * Add one or more new CSS rules to our local styleSheet. `this.styleSheet`
	 * is not managed by JSS.
	 *
	 * @method addRules
	 * @param  {Array} rules Collection of rules to be added to the styleSheet.
	 */
	addRules(rules) {
		for (let i = 0; i < rules.length; i += 1) {
			let j = 1;
			let rule = rules[i];
			const selector = rule[0];
			let propStr = '';

			// If the second argument of a rule is an array of arrays, correct our variables.
			if (Array.isArray(rule[1][0])) {
				rule = rule[1];
				j = 0;
			}

			for (const pl = rule.length; j < pl; j += 1) {
				const prop = rule[j];
				propStr += `${prop[0]}: ${prop[1]}${(prop[2] ? ' !important' : '')};\n`;
			}

			// Insert CSS Rule
			this.styleSheet.insertRule(`${selector}{${propStr}}`, this.styleSheet.cssRules.length);
		}
	}


	/**
	 * [parseDocumentStyles description]
	 * @method parseDocumentStyles
	 * @return {Promise}
	 */
	async parseDocumentStyles() {
		const { products } = this.props;
		const { activeLayoutType, layouts } = this.state;

		// Grab the JSS from the active layout.
		const layoutJSS = get(layouts[activeLayoutType], 'jss');

		// Gather all entries with a className that starts with `font__`. This
		// will help us collect all included style IDs.
		const fontClasses = pickBy(layoutJSS, (className, index) => index.startsWith(Prefix.font));

		// Boil fontClasses down to just the keys (i.e. `font__Whitney--424`).
		// We will get the font ID from the className.
		const classes = keys(fontClasses);

		// Gather all styleIDs from the layoutJSS
		const familyNames =
			chain(classes)
				.map(item => {
					const parts = fontDetailsFromClassName(item);
					return parts[1].replace(/-/, ' ');
				})
				.uniq(familyNames)
				.value();

		const familyRecords = filter(products, item => includes(familyNames, item.attributes.name.replace(Exceptions.nitroTurbo, '')));

		const familyFeatures = map(familyRecords, record => parseInt(record.id, 10));

		await this.processNewFeatures(familyFeatures);
	}


	async processNewFeatures(array) {
		// map array to promises
		const promises = array.map(this.processFeature);
		// wait until all promises are resolved
		await Promise.all(promises);
	}


	async processFeature(item) {
		await this.state.getProductFeatures(item);
	}


	/**
	 * Collects family names from JSS classes and converts them to their ID
	 * counterparts.
	 *
	 * @method getProductIDsFromName
	 * @return {Array}              Array of family and style IDs.
	 */
	getProductIDsFromName(cssClasses) {
		const { products } = this.props;

		// Gather all entries with a className that starts with `font__`. This
		// will help us collect all included style IDs.
		const fontClasses = filter(cssClasses, (className) => className.startsWith(Prefix.font));

		// Gather all styleIDs from the layoutJSS
		const productIDs = map(fontClasses, item => {
			const parts = fontDetailsFromClassName(item);

			const familyRecords =
				find(products, product =>
					product.attributes.name.replace(Exceptions.nitroTurbo, '') === parts[1].replace(/-/, ' ')
				);
			return {
				familyID: parseInt(familyRecords.id, 10),
				styleID: parseInt(parts[2], 10)
			};
		});

		return productIDs;
	}


	onFavorite(ids) {
		const { onToggleFavorite } = this.props;
		onToggleFavorite({ itemID: ids });
	}

	filenamesFromCSSClasses(cssClasses) {
		const { productFeatures } = this.state;

		if (isEmpty(productFeatures)) {
			this.setState({ isLoading: false, loadingError: true });
			return null;
		}

		const productIDs = this.getProductIDsFromName(cssClasses);

		// Get only filenames from the collection
		// — start by selecting the page’s productLine’s styles
		// — filter() those styles by checking the styleIDs from the JSS against the style IDs in the page’s productLine
		// — map() returns only the style’s `value`, which should be a snakeCase value like “Peristyle-Semibold”
		return chain(productIDs)
			.map(item => {
				try {
					const family = productFeatures[item.familyID];
					return find(family.styles, style => style.id === item.styleID).value;
				} catch (error) {
					if (process.env.NODE_ENV === 'development' && process.env.LOG_ERRORS) {
						console.error(error); // eslint-disable-line no-console
					}
					return undefined;
				}
			})
			.compact()
			.value();
	}

	/**
	 * Gather all WOFF filenames for the active layout, generate `@font-face`
	 * rules for each, add those rules to the stylesheet, and load them with
	 * FontFaceObserver.
	 *
	 * @method loadWOFFs
	 * @return {Promise} Return Promise created by FontFaceObserver.
	 */
	async loadWOFFs(cssClasses) {
		const fileNames = this.filenamesFromCSSClasses(cssClasses);
		let woffs = [];

		forEach(fileNames, fileName => {
			woffs = [
				...woffs,
				`${fileName}-TDB`,
				`${fileName}-TDA`
			];
		});

		// Add the generated CSS rules to the local StyleSheet.
		if (this.isMounted) {
			await loadWOFFs(woffs, {
				error: () => {
					if (this.isMounted) {
						this.setState({ isLoading: false, loadingError: true });
					}
				}
			});
		}
	}

	async loadDefaultLabelFont() {
		const defaultLabelFont = map(DefaultLabelFont, style => style[1][1]);
		const woffs = [...defaultLabelFont];
		// Add the generated CSS rules to the local StyleSheet.
		await loadWOFFs(woffs, {
			error: () => {
				this.setState({ isLoading: false, loadingError: true });
			}
		});
	}

	addWOFFRules(cssClasses) {
		const fileNames = this.filenamesFromCSSClasses(cssClasses);

		let rules = [];

		forEach(fileNames, filename => {
			rules = [
				...rules,
				[
					'@font-face',
					['font-family', `${filename}-TDB`],
					['font-weight', 'normal'],
					['font-style', 'normal'],
					['src', `url(/overview/fonts/woff/${filename}-TD-Space.woff)`]
				], [
					'@font-face',
					['font-family', `${filename}-TDA`],
					['font-weight', 'normal'],
					['font-style', 'normal'],
					['src', `url(/overview/fonts/woff/${filename}-TD.woff)`]
				]
			];
		});

		rules = [
			...rules,
			...DefaultLabelFont
		];

		// Add the generated CSS rules to the local StyleSheet.
		this.addRules(rules);
	}

	handleOnBlur() {
		this.setState({ editorIsFocused: false });
	}


	handleOnFocus() {
		this.setState({ editorIsFocused: true });
	}


	render() {
		const {
			breakpoints,
			error,
			productLine,
			products,
			suggestedPairings
		} = this.props;

		const {
			activeLayoutType,
			editorIsFocused,
			favoritedPackageIDs,
			fontsFullyLoaded,
			fontsPartiallyLoaded,
			layoutIsInjected,
			loadingError,
			interactionInitiated,
			updateLayout
		} = this.state;

		const title = `${productLine.attributes.name} Fonts`;

		return (
			<div className={ CSS.overviewWindow }>
				<Helmet
					meta={ [
						{ name: 'viewport', content: 'user-scalable=no, width=device-width, initial-scale=1, maximum-scale=1' },
						{ name: 'HandheldFriendly', content: 'true' }
					] }
					title={ title }
				>
					<meta content={ title } name="twitter:title" />
					<meta content={ title } property="og:title" />
				</Helmet>

				<OverviewProvider value={ this.state }>
					<ErrorBoundary>
						<Editor
							activeLayoutType={ activeLayoutType }
							addRules={ this.addRules }
							editorRef={ this.Editor }
							error={ error }
							fontsLoaded={ fontsPartiallyLoaded }
							immutableState={ this.state }
							layoutIsInjected={ layoutIsInjected }
							products={ products }
							setErrorModal={ this.props.setErrorModal }
							sheets={ this.sheet }
							updateLayout={ updateLayout }
						/>
					</ErrorBoundary>

					{ (!fontsFullyLoaded || loadingError) &&
						<Loading
							className={ fontsPartiallyLoaded ? CSS.partiallyLoaded : CSS.loading }
							delayDisplayTime={ 200 }
							errorTriggered={ loadingError }
							timeoutDuration={ 0 }
						/>
					}

					{
						!isEmpty(suggestedPairings) && !isEmpty(get(suggestedPairings, '0.svg')) &&
						<SuggestedPairings
							favoritedPackageIDs={ favoritedPackageIDs }
							onFavorite={ this.onFavorite }
							suggestedPairings={ suggestedPairings }
						/>
					}

					<ErrorBoundary>
						<Controls
							breakpoints={ breakpoints }
							className={
								classNames(CSS.controls,
									{
										[CSS['controls--interactionInitiated']]: interactionInitiated,
										[CSS['controls--isFocused']]: editorIsFocused
									}
								)
							}
							editorIsFocused={ editorIsFocused }
							productLine={ productLine }
							products={ products }
						/>
					</ErrorBoundary>
				</OverviewProvider>
			</div>
		);
	}
}

// Overview.contextType = OverviewContext;

Overview.propTypes = {
	breakpoints: PropTypes.object.isRequired,
	error: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.shape({ type: PropTypes.string, message: PropTypes.string })
	]),
	favoritedPackageIDs: PropTypes.array,
	onClearTryLayout: PropTypes.func.isRequired,
	onLoadTryLayout: PropTypes.func.isRequired,
	onSetEditorControls: PropTypes.func.isRequired,
	onToggleFavorite: PropTypes.func.isRequired,
	productLine: PropTypes.object,
	products: PropTypes.array,
	route: PropTypes.object.isRequired,
	router: PropTypes.object.isRequired,
	setErrorModal: PropTypes.func.isRequired,
	suggestedPairings: PropTypes.array,
	tryLayout: PropTypes.object // eslint-disable-line react/no-unused-prop-types
};

Overview.defaultProps = {
	error: null,
	favoritedPackageIDs: null,
	productLine: null,
	products: null,
	suggestedPairings: null,
	tryLayout: null
};

const mapStateToProps = createStructuredSelector({
	breakpoints: selectBreakpoints(),
	error: selectError(),
	favoritedPackageIDs: selectFavoritedPackageIDs(),
	productLine: selectProductLine(),
	products: selectProducts(),
	suggestedPairings: selectSuggestedPairings(),
	tryLayout: selectTryLayout(),
	tryLayoutError: selectTryLayoutError()
});

function mapDispatchToProps(dispatch) {
	return {
		onClearTryLayout: () => dispatch(clearTryLayout()),
		onLoadTryLayout: () => dispatch(loadTryLayout()),
		onSetEditorControls: (data) => dispatch(setEditorControlsOpen(data)),
		onToggleFavorite: (data, callback) => dispatch(toggleFavorite(data, callback)),
		setErrorModal: (message) => dispatch(setErrorModal(message)),
		dispatch
	};
}


export default connect(mapStateToProps, mapDispatchToProps)(Overview);
